import React, { useEffect, useState, useCallback } from 'react';
import { Render } from '@puckeditor/core';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xaodao.io/api';

// Helper function to render rich text with basic markdown support
const renderRichText = (text) => {
  if (!text) return null;

  const lines = text.split('\n');

  return lines.map((line, lineIndex) => {
    let remaining = line;

    // Process bold: **text** or __text__
    remaining = remaining.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');

    // Process italic (but not inside bold)
    remaining = remaining.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');

    // Process links: [text](url)
    remaining = remaining.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" style="color: #6366f1; text-decoration: underline;">$1</a>'
    );

    return (
      <span
        key={lineIndex}
        dangerouslySetInnerHTML={{
          __html: remaining + (lineIndex < lines.length - 1 ? '<br/>' : ''),
        }}
      />
    );
  });
};

// Puck render config - portable components using inline styles
// Simplified to match Hero-only puckConfig
const puckRenderConfig = {
  components: {
    Hero: {
      render: (props) => {
        const {
          title = '',
          description = '',
          subtitle = '',
          heroImage = '',
          imageAlt = 'Hero image',
          align = 'center',
          ctaText = '',
          ctaLink = '#',
        } = props;

        const descriptionText = description || subtitle;

        const hasImage = heroImage && heroImage.trim() !== '';
        const isHorizontalLayout = align !== 'center' && hasImage;
        const imageOnLeft = align === 'right';

        const contentSection = (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: align === 'center' ? 'center' : 'flex-start',
              textAlign: align === 'center' ? 'center' : 'left',
              flex: isHorizontalLayout ? '1' : undefined,
              maxWidth: isHorizontalLayout ? '50%' : '100%',
            }}
          >
            {title && (
              <h1
                style={{
                  fontSize: '36px',
                  fontWeight: 700,
                  color: '#ffffff',
                  margin: 0,
                  marginBottom: '16px',
                }}
              >
                {title}
              </h1>
            )}
            {descriptionText && (
              <div
                style={{
                  fontSize: '18px',
                  lineHeight: 1.7,
                  color: 'rgba(255, 255, 255, 0.75)',
                  maxWidth: align === 'center' ? '600px' : '100%',
                  marginBottom: ctaText ? '24px' : '0',
                }}
              >
                {renderRichText(descriptionText)}
              </div>
            )}
            {ctaText && (
              <a
                href={ctaLink || '#'}
                style={{
                  display: 'block',
                  padding: '14px 28px',
                  backgroundColor: '#6366f1',
                  color: '#0B0F19',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px',
                  textAlign: 'center',
                  width: 'fit-content',
                }}
              >
                {ctaText}
              </a>
            )}
          </div>
        );

        const imageSection = hasImage ? (
          <div
            style={{
              flex: isHorizontalLayout ? '1' : undefined,
              maxWidth: isHorizontalLayout ? '45%' : '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: !isHorizontalLayout ? '32px' : undefined,
            }}
          >
            <img
              src={heroImage}
              alt={imageAlt || 'Hero image'}
              style={{
                maxWidth: '100%',
                maxHeight: isHorizontalLayout ? '400px' : '300px',
                height: 'auto',
                borderRadius: '12px',
                objectFit: 'cover',
              }}
            />
          </div>
        ) : null;

        return (
          <div
            style={{
              position: 'relative',
              minHeight: 'auto',
              background: 'transparent',
              borderRadius: '12px',
              overflow: 'hidden',
              margin: '0 0 24px 0',
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: isHorizontalLayout ? 'row' : 'column',
                flexWrap: isHorizontalLayout ? 'wrap' : undefined,
                justifyContent: 'center',
                alignItems: 'center',
                gap: isHorizontalLayout ? '48px' : '0',
                padding: '48px 32px',
                minHeight: 'auto',
              }}
            >
              {imageOnLeft ? (
                <>
                  {imageSection}
                  {contentSection}
                </>
              ) : (
                <>
                  {contentSection}
                  {imageSection}
                </>
              )}
            </div>
          </div>
        );
      },
    },

    Heading: {
      render: (props) => {
        const { text = '', level = 'h2', align = 'left' } = props;
        const Tag = level;
        const componentStyles = {
          h1: { fontSize: '32px', fontWeight: 700, marginBottom: '16px' },
          h2: { fontSize: '24px', fontWeight: 600, marginBottom: '12px' },
          h3: { fontSize: '20px', fontWeight: 600, marginBottom: '8px' },
        };
        return (
          <Tag
            style={{
              ...componentStyles[level],
              textAlign: align,
              color: '#ffffff',
            }}
          >
            {text}
          </Tag>
        );
      },
    },

    Text: {
      render: (props) => {
        const { text = '', align = 'left' } = props;
        return (
          <p
            style={{
              fontSize: '16px',
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.75)',
              textAlign: align,
              marginBottom: '16px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {text}
          </p>
        );
      },
    },

    Image: {
      render: (props) => {
        const { src = '', alt = 'Image', caption = '' } = props;
        return (
          <figure style={{ margin: '24px 0' }}>
            {src && (
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                }}
              />
            )}
            {caption && (
              <figcaption
                style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                }}
              >
                {caption}
              </figcaption>
            )}
          </figure>
        );
      },
    },

    Divider: {
      render: () => (
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            margin: '32px 0',
          }}
        />
      ),
    },

    Callout: {
      render: (props) => {
        const { text = '', type = 'info' } = props;
        const colors = {
          info: { bg: 'rgba(99, 102, 241, 0.1)', border: '#6366f1' },
          warning: { bg: 'rgba(212, 192, 96, 0.1)', border: '#d4c060' },
          success: { bg: 'rgba(24, 235, 184, 0.1)', border: '#18ebb8' },
        };
        const color = colors[type] || colors.info;

        return (
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: color.bg,
              borderLeft: `4px solid ${color.border}`,
              borderRadius: '0 8px 8px 0',
              margin: '16px 0',
            }}
          >
            <p style={{ color: '#ffffff', margin: 0, lineHeight: 1.6 }}>{text}</p>
          </div>
        );
      },
    },

    ButtonLink: {
      render: (props) => {
        const { text = 'Click Here', href = '#', variant = 'primary' } = props;
        const buttonStyles = {
          primary: {
            backgroundColor: '#6366f1',
            color: '#0B0F19',
            border: 'none',
          },
          secondary: {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: 'none',
          },
          outline: {
            backgroundColor: 'transparent',
            color: '#6366f1',
            border: '2px solid #6366f1',
          },
        };

        return (
          <a
            href={href}
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500,
              ...buttonStyles[variant],
            }}
          >
            {text}
          </a>
        );
      },
    },
  },
};

const NAV_LINKS = [
  { label: 'About', href: '/#about' },
  { label: 'Announcements', href: '/announcements', isActive: true },
  { label: 'Join', href: '/#join' },
  { label: 'Proposals', href: '/#proposals' },
  { label: 'Governance Process', href: '/#governance' },
  { label: 'Grants', href: '/#grants' },
  { label: 'Contact', href: 'mailto:membership@xaodao.io', isExternal: true },
];

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 2,
    total: 0,
    totalPages: 0,
  });
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchAnnouncements = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: pagination.limit.toString(),
        });

        const response = await fetch(
          `${API_BASE_URL}/announcements/public?${params.toString()}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        const result = await response.json();

        if (response.ok && result.success) {
          const responseData = result.data?.data || result.data;
          setAnnouncements(responseData.announcements || []);
          setPagination({
            page: responseData.page || 1,
            limit: responseData.limit || 5,
            total: responseData.total || 0,
            totalPages: responseData.totalPages || 0,
          });
        } else {
          setError(result.error || result.message || 'Failed to fetch announcements');
        }
      } catch (err) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit]
  );

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const parseContent = (content) => {
    if (typeof content === 'string') {
      try {
        return JSON.parse(content);
      } catch {
        return { root: { props: {} }, content: [] };
      }
    }
    return content;
  };

  const renderNavLink = (link, index) => {
    if (link.isExternal) {
      return (
        <a
          key={index}
          href={link.href}
          className={`hover:text-white transition-colors ${
            link.isActive ? 'text-white font-semibold' : ''
          }`}
        >
          {link.label}
        </a>
      );
    }

    if (link.href.startsWith('/#')) {
      return (
        <a
          key={index}
          href={link.href}
          className={`hover:text-white transition-colors ${
            link.isActive ? 'text-white font-semibold' : ''
          }`}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        key={index}
        to={link.href}
        className={`hover:text-white transition-colors ${
          link.isActive ? 'text-white font-semibold' : ''
        }`}
      >
        {link.label}
      </Link>
    );
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => fetchAnnouncements(i)}
          className={`inline-flex items-center justify-center min-w-[40px] h-[40px] px-3.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 ${
            pagination.page === i
              ? 'bg-indigo-500 border-indigo-500 text-white border'
              : 'bg-transparent border border-white/20 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/30'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-10 pt-6 border-t border-white/10 flex-wrap">
        <button
          onClick={() => fetchAnnouncements(pagination.page - 1)}
          disabled={pagination.page <= 1}
          className="inline-flex items-center justify-center min-w-[40px] h-[40px] px-3.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 bg-transparent border border-white/20 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        {pages}
        <button
          onClick={() => fetchAnnouncements(pagination.page + 1)}
          disabled={pagination.page >= pagination.totalPages}
          className="inline-flex items-center justify-center min-w-[40px] h-[40px] px-3.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 bg-transparent border border-white/20 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-10 h-10 border-[3px] border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mb-4" />
          <p className="text-white/60 text-[15px]">Loading announcements...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-red-500/5 border border-red-500/20 rounded-2xl">
          <svg
            className="w-12 h-12 mb-4 text-red-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3 className="text-lg font-semibold text-red-500 mb-2">
            Unable to load announcements
          </h3>
          <p className="text-sm text-white/60 mb-5">{error}</p>
          <button
            onClick={() => fetchAnnouncements()}
            className="px-5 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-500/15 hover:border-red-500/50"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (announcements.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white/[0.03] border border-white/10 rounded-2xl">
          <svg
            className="w-16 h-16 mb-5 text-white/30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="3,11 22,2 13,21 11,13 3,11" />
          </svg>
          <h3 className="text-xl font-semibold text-white mb-2">No announcements yet</h3>
          <p className="text-[15px] text-white/50">Check back later for updates from the DAO.</p>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-8">
          {announcements.map((announcement) => (
            <div key={announcement.id}>
              {/* Slack-style timestamp divider */}
              <div className="flex items-center mb-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs font-medium text-white/50 whitespace-nowrap px-5 py-2 border border-white/10 rounded-full">
                  {formatDate(announcement.publishedAt || announcement.createdDate)}
                </span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <article className="rounded-2xl overflow-hidden transition-colors duration-200">
                <div className="announcement-puck-content">
                  <Render config={puckRenderConfig} data={parseContent(announcement.content)} />
                </div>
              </article>
            </div>
          ))}
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121828' }}>
      {/* Navigation Header */}
      <nav className="w-full" style={{ backgroundColor: '#0d1117' }}>
        {/* Desktop nav */}
        <div className="hidden md:flex max-w-6xl mx-auto px-6 py-5 items-center justify-between">
          <Link to="/">
            <img src="/xao_dao_text_logo.svg" alt="XAO.DAO Logo" className="h-10" />
          </Link>
          <div className="flex space-x-8 font-['Inter',_sans-serif] text-sm text-white/75">
            {NAV_LINKS.map((link, i) => renderNavLink(link, i))}
          </div>
        </div>

        {/* Mobile nav */}
        <div className="flex md:hidden w-full items-center justify-between px-4 py-4">
          <Link to="/">
            <img src="/xao_dao_text_logo.svg" alt="XAO.DAO Logo" className="w-32 h-32" />
          </Link>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-12 h-12 flex flex-col items-center justify-center"
          >
            <span className="block w-8 h-1 bg-white rounded mb-2" />
            <span className="block w-8 h-1 bg-white rounded mb-2" />
            <span className="block w-8 h-1 bg-white rounded" />
          </button>
        </div>

        {/* Mobile menu dropdown */}
        {menuOpen && (
          <div className="fixed top-0 left-0 w-full min-h-fit bg-black/90 z-30 flex flex-col items-center justify-center py-10">
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-4 left-4 text-white text-3xl font-bold"
              aria-label="Close menu"
            >
              &times;
            </button>
            <div className="flex flex-col items-center space-y-4">
              {NAV_LINKS.map((link, i) => (
                <div key={i} className="text-center">
                  {link.isExternal ? (
                    <a
                      href={link.href}
                      onClick={() => setMenuOpen(false)}
                      className={`font-['Inter',_sans-serif] text-lg ${
                        link.isActive ? 'text-white font-semibold' : 'text-white/75'
                      }`}
                    >
                      {link.label}
                    </a>
                  ) : link.href.startsWith('/#') ? (
                    <a
                      href={link.href}
                      onClick={() => setMenuOpen(false)}
                      className={`font-['Inter',_sans-serif] text-lg ${
                        link.isActive ? 'text-white font-semibold' : 'text-white/75'
                      }`}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => setMenuOpen(false)}
                      className={`font-['Inter',_sans-serif] text-lg ${
                        link.isActive ? 'text-white font-semibold' : 'text-white/75'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Page Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-[28px] font-bold text-white mb-2">Announcements</h1>
          <p className="text-base text-white/60">
            Stay updated with the latest news from XAO DAO
          </p>
        </div>
        {renderContent()}
      </div>
    </div>
  );
}
